// The 20-theme starting catalog for brand creation. Themes pair colors for contrast,
// not complement: the action button must pop against the header it sits on, either by
// hue family or by a large lightness delta. Each theme carries explicit roles rather
// than a generic palette. Keep in sync with the experiments/brand-builder mockups
// until the catalog moves behind an API.

export interface IBrandTheme {
  name: string;
  tag: string;
  primary: string;
  headerBg: string;
  headerFg: string;
  btnBg: string;
  btnFg: string;
  accent: string;
}

export const BRAND_THEMES: IBrandTheme[] = [
  {name: 'Verdocs Classic', tag: 'default', primary: '#654DCB', headerBg: '#FFFFFF', headerFg: '#33364B', btnBg: '#50BE80', btnFg: '#FFFFFF', accent: '#654DCB'},
  {name: 'Coastal', tag: 'cool', primary: '#1D7DB8', headerBg: '#16324F', headerFg: '#F2F6FA', btnBg: '#F2A541', btnFg: '#201400', accent: '#1D7DB8'},
  {name: 'Deep Sea', tag: 'cool', primary: '#12616F', headerBg: '#0E3E47', headerFg: '#E9F4F5', btnBg: '#7FD1B9', btnFg: '#0B2E28', accent: '#26A0B4'},
  {name: 'Glacier', tag: 'cool', primary: '#0F5C8C', headerBg: '#E9F1F6', headerFg: '#143A52', btnBg: '#0F5C8C', btnFg: '#FFFFFF', accent: '#4C9FD8'},
  {name: 'Ember', tag: 'warm', primary: '#A63A3A', headerBg: '#6E2A2A', headerFg: '#FAEFEA', btnBg: '#E8B23A', btnFg: '#33230A', accent: '#C25E4C'},
  {name: 'Cocoa', tag: 'warm', primary: '#6D4C41', headerBg: '#4E342E', headerFg: '#F5EDE9', btnBg: '#A8C09A', btnFg: '#1E2A18', accent: '#8FAE82'},
  {name: 'Citrus', tag: 'playful', primary: '#E85D04', headerBg: '#FFF6E8', headerFg: '#5A3A12', btnBg: '#E85D04', btnFg: '#FFFFFF', accent: '#F48C06'},
  {name: 'Flamingo', tag: 'playful', primary: '#D81E5B', headerBg: '#FDEFF3', headerFg: '#55152C', btnBg: '#D81E5B', btnFg: '#FFFFFF', accent: '#F26D99'},
  {name: 'Orchid', tag: 'playful', primary: '#6C3082', headerBg: '#3B1E4E', headerFg: '#F3EBF8', btnBg: '#C7E85D', btnFg: '#24300A', accent: '#9B59B6'},
  {name: 'Fern', tag: 'earthy', primary: '#3A6B35', headerBg: '#2E3B2D', headerFg: '#EEF3EC', btnBg: '#E9C46A', btnFg: '#2E2508', accent: '#6A994E'},
  {name: 'Terracotta', tag: 'earthy', primary: '#9A4A31', headerBg: '#6E3423', headerFg: '#F9EFE7', btnBg: '#3FBFB0', btnFg: '#04302B', accent: '#C96F4A'},
  {name: 'Sandstone', tag: 'earthy', primary: '#8C5A2B', headerBg: '#EFE6D8', headerFg: '#4A3B22', btnBg: '#8C3B1B', btnFg: '#FFF6EE', accent: '#B07D3F'},
  {name: 'Meadow', tag: 'calm', primary: '#3A7D44', headerBg: '#EFF4ED', headerFg: '#2F3E2E', btnBg: '#3A7D44', btnFg: '#FFFFFF', accent: '#62A87C'},
  {name: 'Slate & Coral', tag: 'calm', primary: '#46586A', headerBg: '#3E4C59', headerFg: '#F3F5F7', btnBg: '#F76C5E', btnFg: '#FFFFFF', accent: '#F76C5E'},
  {name: 'Violet Hour', tag: 'premium', primary: '#5D3FA3', headerBg: '#322A4F', headerFg: '#F1EEF9', btnBg: '#C9B6F7', btnFg: '#241539', accent: '#7C5CD1'},
  {name: 'Midnight', tag: 'premium', primary: '#4F46E5', headerBg: '#101322', headerFg: '#E8EAF6', btnBg: '#4F46E5', btnFg: '#FFFFFF', accent: '#7B74F0'},
  {name: 'Regal', tag: 'premium', primary: '#1E2749', headerBg: '#1E2749', headerFg: '#EDEFF7', btnBg: '#F2F4FA', btnFg: '#1E2749', accent: '#45538C'},
  {name: 'Porcelain', tag: 'minimal', primary: '#2F2F2F', headerBg: '#F7F7F5', headerFg: '#262626', btnBg: '#111827', btnFg: '#FFFFFF', accent: '#6B7280'},
  {name: 'Graphite', tag: 'minimal', primary: '#383838', headerBg: '#2B2B2B', headerFg: '#EFEFEF', btnBg: '#E5E5E5', btnFg: '#1A1A1A', accent: '#8A8A8A'},
  {name: 'Pacific', tag: 'bold', primary: '#0B6E8E', headerBg: '#056280', headerFg: '#EAF6FA', btnBg: '#FF5964', btnFg: '#FFFFFF', accent: '#23A5C9'},
];

export const BRAND_THEME_TAGS = ['all', 'cool', 'warm', 'earthy', 'playful', 'calm', 'premium', 'minimal', 'bold', 'default'];

// Brand keys must match the API's ^[a-z0-9]([a-z0-9-]*[a-z0-9])?$ rule.
export const BRAND_KEY_PATTERN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

export const slugifyKey = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);

export const brandInitials = (name: string | null | undefined, key: string) => {
  const source = (name || key).trim();
  const words = source.split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
};

// Stable tile color for brands with no logo and no primary color.
const TILE_COLORS = ['#4F46E5', '#0D6C9C', '#1A2B4A', '#0F766E', '#B45309', '#BE185D', '#6D28D9', '#334155'];

export const brandTileColor = (brand: {primary_color?: string | null; key: string}) => {
  if (brand.primary_color) return brand.primary_color;
  let hash = 0;
  for (const ch of brand.key) hash = (hash * 31 + ch.charCodeAt(0)) % 997;
  return TILE_COLORS[hash % TILE_COLORS.length];
};

// Readable text color for an arbitrary background (YIQ threshold).
export const contrastText = (hex: string) => {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 >= 140 ? '#111827' : '#FFFFFF';
};
